/* eslint-disable import/no-relative-packages */
import React, { useRef } from "react";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import RichText from "@qlik/qlik/src/components/RichText";
import { TYPE_MARKETO_FORM } from "@qlik/qlik/src/lib/constants/contentful-types";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";
import MarketoForm from "../../MarketoForm/MarketoForm";

/**
 * StandardMarketoForm is a React component that renders a Marketo Form.
 *
 * @component
 * @param {string} props.data.backgroundColor The background color for the section.
 * @param {Object[]} props.data.backgroundImage The background image for the section.
 * @param {Object} props.data.additionalConfiguration Additional configuration for the section.
 * @param {Object[]} props.data.cardItems The card items to be displayed in the section.
 *
 * @returns {JSX.Element} A section with a Marketo form.
 */
const StandardMarketoForm = ({ data }) => {
    const { backgroundColor, backgroundImage, additionalConfiguration, cardItems = [], customId } = data ?? {};
    const sectionPaddingClassName = getSpacingClassName(additionalConfiguration, "py-15 lg:py-30");
    const { sectionColorMapping } = getColorClassName(backgroundColor);
    const sectionRef = useRef(null);

    const textContentCard = cardItems?.[0];
    const formCard = cardItems?.find((card) => card.__typename === TYPE_MARKETO_FORM);

    const isTwoColumn = textContentCard && formCard;

    return (
        <section
            ref={sectionRef}
            className={`relative overflow-hidden scroll-m-15 ${sectionPaddingClassName} ${sectionColorMapping.bgColor ||
                "bg-white"} ${styles.StandardMarketoForm}`}
            id={customId}
        >
            <div className="container relative z-2">
                <div className="row">
                    {textContentCard && (
                        <div className={`col-12 lg:pr-[65px] ${isTwoColumn ? "md:col-6 lg:col-7" : ""}`}>
                            {textContentCard.eyebrowText && (
                                <p className={`tag-2 uppercase text-sm mb-5 ${sectionColorMapping.secondaryTextColor}`}>
                                    {textContentCard.eyebrowText}
                                </p>
                            )}
                            {textContentCard.title && (
                                <h1
                                    className={`title-2 ${sectionColorMapping.primaryTextColor}`} // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: renderSanitizedHtmlContent(textContentCard.title),
                                    }}
                                />
                            )}
                            {textContentCard?.description && (
                                <p className={`mt-7.5 ${sectionColorMapping.primaryTextColor || "text-blue"}`}>
                                    {textContentCard.description.description}
                                </p>
                            )}
                            {textContentCard?.body?.raw && (
                                <RichText
                                    data={textContentCard}
                                    className={`bodyContent mt-7.5 ${styles.bodyContent}`}
                                />
                            )}
                            {textContentCard?.cta?.map((link) => (
                                <Cta key={link.id} data={link} className="mb-7.5 mt-3.75" />
                            ))}
                            {textContentCard?.media?.[0] && (
                                <div>
                                    <Media
                                        media={textContentCard?.media?.[0]}
                                        className="h-auto max-h-225 max-w-88 rounded-sm md:max-w-none"
                                    />
                                    {textContentCard?.media?.[0]?.mediaCaptionText?.mediaCaptionText && (
                                        <span className="block body text-blue mt-7.5">
                                            {textContentCard?.media?.[0]?.mediaCaptionText?.mediaCaptionText}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {formCard && (
                        <div className={`col-12 ${isTwoColumn ? "md:col-6 lg:col-5 max-md:mt-10" : ""}`}>
                            <div
                                className={`px-5 py-11 lg:p-10.25 ${
                                    sectionColorMapping?.bgColor
                                        ? "bg-white border border-stone/50 rounded shadow-xxl"
                                        : "bg-blue"
                                }`}
                            >
                                <MarketoForm {...formCard} isDark={!sectionColorMapping?.bgColor} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {backgroundImage?.length > 0 && backgroundImage?.[0]?.resource_type === "image" && (
                <>
                    <Media
                        key={backgroundImage?.[0]?.id}
                        link={backgroundImage?.[0]?.secure_url}
                        width={backgroundImage?.[0]?.width}
                        height={backgroundImage?.[0]?.height}
                        className="absolute z-1 left-0 top-0 object-left-top max-h-full max-w-full"
                    />
                </>
            )}
        </section>
    );
};

export default StandardMarketoForm;
